import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useRemoveAppToken } from '@/hooks/useRemoveAppToken';
import { AppToken } from '@/services/AppTokensService';

interface RemoveTokenModalProps {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  token: AppToken | null;
}

export function RemoveTokenModal({
  open,
  onOpenChange,
  token,
}: RemoveTokenModalProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.Settings.components.AppTokens.components.RemoveConfirmationModal',
  });
  const { removeAppToken } = useRemoveAppToken();

  async function confirmRemoveToken() {
    if (!token?.id) return;

    try {
      await removeAppToken(token.id);
      onOpenChange(false);
    } catch (error) {
      toast.error(t('deleteError'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>

          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />

            <AlertDescription>{t('warningDescription')}</AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('cancelButton')}
          </Button>

          <Button variant="destructive" onClick={confirmRemoveToken}>
            {t('confirmButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
